import React, { useEffect, useState } from 'react';
import { addDoc, deleteDoc, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { timeCapsuleDoc, timeCapsulesColl } from '../db';
import { Screen, UserProfile } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }

interface Capsule {
  id: string;
  title: string;
  message: string;
  openDate: string;
  createdBy: string;
  createdByName: string;
}

function isOpen(date: string) {
  return new Date(`${date}T00:00:00`).getTime() <= Date.now();
}

function formatDate(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function TimeCapsule({ user, navigate }: Props) {
  const [capsules, setCapsules] = useState<Capsule[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [openDate, setOpenDate] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(timeCapsulesColl(user.coupleId), orderBy('openDate', 'asc'));
    return onSnapshot(q, snap => setCapsules(snap.docs.map(d => ({ id: d.id, ...d.data() } as Capsule))));
  }, [user.coupleId]);

  const add = async () => {
    if (!title.trim() || !message.trim() || !openDate) return;
    await addDoc(timeCapsulesColl(user.coupleId), {
      title: title.trim(),
      message: message.trim(),
      openDate,
      createdBy: user.uid,
      createdByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setTitle('');
    setMessage('');
    setOpenDate('');
    setOpen(false);
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('memories')}>←</button>
        <h1 style={styles.title}>Zaman Kapsülü</h1>
      </div>

      <div className="feature-page">
        <section className="feature-hero-card">
          <p className="eyebrow">Geleceğe mesaj</p>
          <h2>Şimdi yaz, zamanı gelince birlikte açın.</h2>
          <span>Doğum günü, buluşma günü, yıl dönümü ya da sadece gelecekteki size küçük bir sürpriz.</span>
          <button className="feature-primary" onClick={() => setOpen(true)}>Kapsül oluştur</button>
        </section>

        <section className="capsule-grid">
          {capsules.map(capsule => {
            const unlocked = isOpen(capsule.openDate);
            return (
              <article key={capsule.id} className={`capsule-card ${unlocked ? 'open' : ''}`}>
                <small>{unlocked ? 'Açıldı' : `Açılacağı gün: ${formatDate(capsule.openDate)}`}</small>
                <h3>{capsule.title}</h3>
                <p>{unlocked ? capsule.message : 'Bu mesaj zamanı gelene kadar kapalı.'}</p>
                <div>
                  <span>{capsule.createdByName}</span>
                  {capsule.createdBy === user.uid && <button onClick={() => deleteDoc(timeCapsuleDoc(user.coupleId, capsule.id))}>Sil</button>}
                </div>
              </article>
            );
          })}
          {capsules.length === 0 && <div className="empty-state">Henüz kapsül yok. Gelecekte açılacak ilk mesajı yazalım.</div>}
        </section>
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2>Kapsül oluştur</h2>
            <p className="modal-sub">Açılacağı güne kadar saklanacak.</p>
            <input placeholder="Başlık" value={title} onChange={e => setTitle(e.target.value)} style={{ marginBottom: 10 }} />
            <input type="date" value={openDate} onChange={e => setOpenDate(e.target.value)} style={{ marginBottom: 10 }} />
            <textarea placeholder="Mesajınız" value={message} onChange={e => setMessage(e.target.value)} style={{ marginBottom: 12 }} />
            <button className="btn btn-primary" onClick={add}>Kapsülü kapat</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
};
